const API_BASE = '/api/v1';

export interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string[]>;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

async function refreshCsrf(): Promise<void> {
  try {
    // Backend route is `@Post('csrf')` (it requires an active session, same as any
    // other authenticated endpoint) — fetch()'s default method is GET, which always
    // 404'd here. That meant csrfToken (an in-memory variable, reset on every page
    // load/reload) never got repopulated after a reload, so a logged-in user's
    // first mutation after refreshing the page would go out without X-CSRF-Token
    // and get rejected by CsrfGuard with 403 "CSRF token missing or invalid".
    const res = await fetch(`${API_BASE}/auth/csrf`, { method: 'POST', credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      csrfToken = data?.data?.csrfToken || null;
    }
  } catch {
    csrfToken = null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (csrfToken === null && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    await refreshCsrf();
  }
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  const options: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };
  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${API_BASE}${path}`, options);
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    // non-JSON response
  }
  if (!response.ok) {
    const err = data as { error?: ApiErrorBody } | null;
    const message = err?.error?.message || 'Permintaan gagal';
    throw new ApiError(message, response.status, err?.error);
  }
  return (data as { data: T }).data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  setCsrf: (token: string | null) => {
    csrfToken = token;
  },
  getCsrf: () => csrfToken,
};
